import {
  fallbackHotelShopAvailabilityTrackingProperties,
  fallbackLodgingInListTrackingProperties,
  type HotelAvailabilityTrackingProperties,
  type HotelRoomRateTrackingProperties,
  type HotelRoomTrackingProperties,
  type HotelSelectedRoomEntityProperties,
  type HotelSelectedRoomRateEntityProperties,
  type HotelShopAvailabilityTrackingProperties,
  type HotelShopTrackingProperties,
  type HotelViewedLodgingInListTrackingProperties,
  logNotFoundTrackingProperties,
  type SelectedRoomInfoTrackingProperties,
} from "@b2bportal/core-tracking";
import {
  getImageUrlsArray,
  toStarRating,
  toStarRatingForAvailableRating,
} from "@b2bportal/core-utilities";
import {
  Amenity,
  CancellationPolicyV2Enum,
  type GuestsSelection,
  HotelStarRating,
  type Lodging,
  PriceChangeDirection,
  type RoomInfoProducts,
  type RoomProduct,
  type Suggestion,
} from "@b2bportal/lodging-api";
import type {
  CancellationPolicyV2,
  LodgingHomesAmenityKind,
  StayType,
} from "@b2bportal/lodging-api";
import type {
  HomeRoomsAndBeds,
  HotelSortOptionEnum,
  PriceRange,
  SearchDetails,
  TrackingProperties,
} from "@overrides/types";
import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {
  HotelsAvailabilitySelectors,
  type HotelsState,
  HotelsShopSelectors,
  type ShopSearch,
} from "../store";

export const getLodgingInListIndex = (
  lodging: Lodging,
  lodgings: Lodging[]
): number | null => {
  const lodgingIndexInList = lodgings.findIndex((l) => lodging === l);
  return lodgingIndexInList !== -1 ? lodgingIndexInList : null;
};

export const getSelectedLodgingInListIndex = createSelector(
  HotelsShopSelectors.getSelectedHotel,
  HotelsAvailabilitySelectors.getHotels,
  getLodgingInListIndex
);

const getHasBreakfast = (lodging: Lodging) => {
  return lodging.lodging.amenities.some(
    ({ amenity }) =>
      amenity === Amenity.BreakfastIncluded ||
      amenity === Amenity.FreeBreakfast ||
      amenity === Amenity.PaidBreakfast
  );
};

export const fallbackSelectedHotelTrackingProperties = {
  hopper_preferred_hotel: false,
  lodging_list_index: -1,
  lodging_id: "",
  city_name: "",
  name: "",
  country: "",
};

export const getHotelShopTrackingProperties = createSelector(
  HotelsShopSelectors.getSelectedHotel,
  getSelectedLodgingInListIndex,
  (lodging: Lodging, lodgingListIndex: number): HotelShopTrackingProperties => {
    if (lodging == null) {
      logNotFoundTrackingProperties("getHotelShopTrackingProperties", "hotels");
      return {
        properties: fallbackSelectedHotelTrackingProperties,
        encryptedProperties: "",
      };
    }
    const hopper_preferred_hotel = lodging.isPreferred;

    const { properties, encryptedProperties } =
      lodging.trackingPropertiesV2 ?? {
        properties: {},
        encryptedProperties: "",
      };

    return {
      properties: {
        ...properties,
        hopper_preferred_hotel,
        city_name: lodging.lodging.city,
        lodging_list_index: lodgingListIndex,
        lodging_id: lodging.lodging.id,
        name: lodging.lodging.name,
        country: lodging.lodging.country,
        breakfast_included_shown: getHasBreakfast(lodging),
      },
      encryptedProperties: encryptedProperties ?? "",
    };
  }
);

export const getSelectedRoomIndexFromRooms = (
  rate: RoomProduct | undefined,
  rooms: RoomInfoProducts[] | undefined
) => {
  if (rate == null || rooms == null) {
    logNotFoundTrackingProperties("getSelectedRoomIndexInShop", "hotels");
    return -1;
  }

  const selectedRoomIndex = rooms.findIndex((room) =>
    room.products.some((product) => product.rateId === rate.rateId)
  );

  return selectedRoomIndex;
};

export const getSelectedRoomIndexInShop = createSelector(
  HotelsShopSelectors.getSelectedRoom,
  HotelsShopSelectors.getRooms,
  (room, rooms) => getSelectedRoomIndexFromRooms(room, rooms?.data)
);

const fallbackRoomInfoTrackingProperties = {
  selectable_rates_count: 0,
  room_type_name: "",
  number_of_photos: 0,
};

export const getTrackingPropertiesFromRoomInfo = (
  roomInfo: RoomInfoProducts | undefined
) => {
  if (roomInfo == null) {
    logNotFoundTrackingProperties(
      "getSelectedRoomInfoTrackingProperties",
      "hotels"
    );
    return fallbackRoomInfoTrackingProperties;
  }

  const roomMediaPhotosCount = getImageUrlsArray(
    roomInfo.roomInfo?.media ?? []
  ).length;

  return {
    selectable_rates_count: roomInfo.products.length,
    room_type_name: roomInfo.roomInfo?.name ?? "",
    number_of_photos: roomMediaPhotosCount,
    room_media_count: roomInfo.roomInfo?.media.length ?? 0,
    room_media_shown: roomMediaPhotosCount > 0,
    ...(roomInfo.trackingPropertiesV2?.properties ?? {}),
  };
};
export const getSelectedRoomInfoTrackingProperties = createSelector(
  HotelsShopSelectors.getSelectedRoomInfo,
  getTrackingPropertiesFromRoomInfo
);

export const getSelectedRoomTrackingProperties = createSelector(
  getHotelShopTrackingProperties,
  getSelectedRoomInfoTrackingProperties,
  getSelectedRoomIndexInShop,
  (
    selectedHotelTrackingProperties: HotelShopTrackingProperties,
    selectedRoomInfo: SelectedRoomInfoTrackingProperties,
    selectedRoomIndex: number
  ): HotelRoomTrackingProperties => {
    const roomHotelTrackingProperties =
      selectedHotelTrackingProperties.properties;
    return {
      properties: {
        /** TODO: Given trackingPropertiesV2 is still not returned by the BFF
         * the selector adds the required properties for room rates related events
         * See: https://hopper-jira.atlassian.net/browse/STAYSVA-356
         */
        ...selectedRoomInfo,
        room_index_in_shop: selectedRoomIndex,
        lodging_name: roomHotelTrackingProperties.name,
        city_name: roomHotelTrackingProperties.city_name,
        country_name: roomHotelTrackingProperties.country,
        lodging_id: roomHotelTrackingProperties.lodging_id,
      },
      encryptedProperties: selectedHotelTrackingProperties.encryptedProperties,
    };
  }
);

export const isCancellationPolicyRefundable = (
  cancellationPolicy?: CancellationPolicyV2["CancellationPolicyV2"]
) => {
  if (cancellationPolicy == null) {
    return false;
  }

  return (
    cancellationPolicy === CancellationPolicyV2Enum.FullyRefundable ||
    cancellationPolicy === CancellationPolicyV2Enum.PartiallyRefundable
  );
};

export const fallbackRoomRateTrackingProperties = {
  refundable: false,
  refundable_type: CancellationPolicyV2Enum.Unknown,
};

export const getRoomRateTrackingProperties = (roomRate: RoomProduct) => ({
  refundable: isCancellationPolicyRefundable(
    roomRate.cancellationPolicyV2?.CancellationPolicyV2
  ),
  refundable_type:
    roomRate.cancellationPolicyV2?.CancellationPolicyV2 ??
    CancellationPolicyV2Enum.Unknown,
  ...(roomRate.trackingPropertiesV2?.properties ?? {}),
});

export const getSelectedRoomRateTrackingProperties = createSelector(
  HotelsShopSelectors.getSelectedRoom,
  (
    selectedRoomRate: RoomProduct | undefined
  ): HotelRoomRateTrackingProperties => {
    if (selectedRoomRate == null) {
      logNotFoundTrackingProperties(
        "getSelectedRoomRateTrackingProperties",
        "hotels"
      );
      return {
        properties: fallbackRoomRateTrackingProperties,
      };
    }

    return {
      properties: getRoomRateTrackingProperties(selectedRoomRate),
      encryptedProperties:
        selectedRoomRate.trackingPropertiesV2.encryptedProperties,
    };
  }
);

export const getShopPriceChange = createSelector(
  (state: HotelsState) => state.hotels.hotelsShop.shop?.lodgingPriceChange,
  (shopPriceChange) => {
    if (!shopPriceChange) {
      return { price_change: 0 };
    }
    const value =
      (shopPriceChange.direction === PriceChangeDirection.Increase ? 1 : -1) *
      Math.abs(shopPriceChange.priceChangePercentage);

    return { price_change: value };
  }
);

const fallbackSearchedLocationTrackingProperties = {
  searched_place_id: "",
  searched_place_term: "",
};

export const getSearchedLocationTrackingProperties = (place?: Suggestion) => {
  if (place == null) {
    logNotFoundTrackingProperties(
      "getSearchedLocationTrackingProperties",
      "hotels"
    );
    return fallbackSearchedLocationTrackingProperties;
  }
  const placeType = place.id;
  if (placeType.Id === "HotelMarket") {
    logNotFoundTrackingProperties(
      "getSearchedLocationTrackingProperties",
      "hotels"
    );
    return fallbackSearchedLocationTrackingProperties;
  }
  if (placeType.Id === "Lodgings") {
    const { lodgingSelection } = placeType;
    switch (lodgingSelection.LodgingSelection) {
      case "Market":
      case "LodgingIds":
      case "Location": {
        return fallbackSearchedLocationTrackingProperties;
      }
      case "Place": {
        return {
          searched_place_id: lodgingSelection.placeId,
          searched_place_term: lodgingSelection.searchTerm,
        };
      }
    }
  }
  logNotFoundTrackingProperties(
    "getSearchedLocationTrackingProperties",
    "hotels"
  );
  return fallbackSearchedLocationTrackingProperties;
};

export const getLodgingAvailabilityTrackingProperties = (
  dateRange: { from?: string; until?: string },
  sort: HotelSortOptionEnum,
  starRatingFilter: HotelStarRating[],
  priceFilter: PriceRange,
  cancellationFilter: boolean,
  amenitiesFilter: Amenity[],
  lodgings: Lodging[],
  guests: GuestsSelection,
  amountOfSelectedFilters: number,
  requestedSearch: SearchDetails | undefined,
  stayTypes: StayType[],
  totalPropertyCount: number | undefined,
  trackingProperties: TrackingProperties[],
  lodgingTypeCount: ReturnType<
    typeof HotelsAvailabilitySelectors.getAvailabilityLodgingTypeCount
  >,
  homeAmenities: LodgingHomesAmenityKind[],
  homeRoomsAndBeds: HomeRoomsAndBeds | undefined
): HotelAvailabilityTrackingProperties => {
  const advance = dayjs(dateRange.from).diff(dayjs(), "day");
  const check_in_date = dayjs(dateRange.from).format("YYYY-MM-DD");
  const check_out_date = dayjs(dateRange.until).format("YYYY-MM-DD");
  const los = dayjs(dateRange.until).diff(dayjs(dateRange.from), "day");
  const sort_order = sort;
  const number_of_filters_applied = amountOfSelectedFilters;
  const searchLocationTrackingProperties =
    requestedSearch != null
      ? getSearchedLocationTrackingProperties(requestedSearch.place)
      : fallbackSearchedLocationTrackingProperties;

  return {
    properties: {
      advance,
      check_in_date,
      check_out_date,
      los,
      number_of_filters_applied,
      sort_order,
      adults_count: guests.adults,
      children_count: guests.children.length,
      children_ages: guests.children,
      applied_filters_filter_amenities: amenitiesFilter,
      applied_filters_filter_star: starRatingFilter.reduce<number[]>(
        (acc, s) => {
          if (s !== HotelStarRating.NotAvailable) {
            acc.push(toStarRatingForAvailableRating(s));
          }
          return acc;
        },
        []
      ),
      number_of_properties: totalPropertyCount ?? 0,
      applied_filters_filter_stay_type: stayTypes,
      available_hotels: lodgingTypeCount.available,
      unavailable_hotels: lodgingTypeCount.unavailable,
      pets_searched: requestedSearch?.guests.pets != null,
      rooms_searched: requestedSearch?.rooms ?? 0,
      room_count: requestedSearch?.rooms ?? 0,
      number_of_guests:
        (requestedSearch?.guests.adults ?? 0) +
        (requestedSearch?.guests.children.length ?? 0),
      applied_filters_filter_vr_amenities: homeAmenities,
      applied_filters_filter_vr_rooms_and_beds: Object.values(
        homeRoomsAndBeds ?? {}
      ).some((f) => f === true),
      available_vr: lodgingTypeCount.homes.available,
      ...searchLocationTrackingProperties,
      ...trackingProperties.reduce((properties, val) => {
        return { ...properties, ...(val.properties ?? {}) };
      }, {}),
    },
    encryptedProperties: trackingProperties.reduce<string[]>((acc, t) => {
      if (t.encryptedProperties != null) {
        acc.push(t.encryptedProperties);
      }
      return acc;
    }, []),
  };
};

export const getLodgingInListTrackingProperties = (
  hotel: Lodging,
  lodgingListIndex: number
): HotelViewedLodgingInListTrackingProperties => {
  return {
    properties: {
      lodging_list_index: lodgingListIndex,
      breakfast_included_shown: getHasBreakfast(hotel),
      city_name: hotel.lodging.city,
      country_name: hotel.lodging.country,
      daily_owed_overall_excluding_taxes_fees_usd:
        hotel.trackingPropertiesV2?.properties
          ?.daily_owed_overall_excluding_taxes_fees_usd,
      daily_owed_overall_including_taxes_fees_usd:
        hotel.trackingPropertiesV2?.properties
          ?.daily_owed_overall_including_taxes_fees_usd,
      im_lodging_name: hotel.lodging.name,
      is_sponsored_hotel: Boolean(hotel.isSponsored),
      lodging_id: hotel.lodging.id,
      lodging_name: hotel.lodging.name,
      lodging_star_rating: toStarRating(hotel.lodging.starRating),
      lodging_trip_advisor_rating:
        hotel.lodging.tripAdvisorReviews?.overallScore,
      lodging_type: hotel.lodgingCollection,
      market_id: hotel.trackingPropertiesV2?.properties?.market_id,
      market_name: hotel.trackingPropertiesV2?.properties?.market_name,
      number_of_photos: hotel.lodging.media.length,
      owed_overall_including_taxes_fees_usd:
        hotel.trackingPropertiesV2?.properties
          ?.owed_overall_including_taxes_fees_usd,
      page_index: 0,
      provider_name: hotel.trackingPropertiesV2?.properties?.provider_name,
      rate_provider: hotel.trackingPropertiesV2?.properties?.rate_provider,
      rate_type: hotel.trackingPropertiesV2?.properties?.rate_type,
    },
  };
};

export const getLodgingTrackingPropertiesByHotelId = createSelector(
  HotelsAvailabilitySelectors.getHotels,
  (_: HotelsState, hotelId: string) => hotelId,
  (hotels, hotelId) => {
    const hotel = hotels.find((hotel) => hotel.lodging.id === hotelId);

    if (!hotel) {
      logNotFoundTrackingProperties(
        "getLodgingTrackingPropertiesByHotelId",
        "hotels"
      );
      return {
        properties: fallbackLodgingInListTrackingProperties.properties,
        encryptedProperties: [],
      };
    }

    const idx = getLodgingInListIndex(hotel, hotels) ?? -1;

    const { trackingPropertiesV2 } = hotel;

    const { properties } = getLodgingInListTrackingProperties(hotel, idx);

    return {
      properties,
      encryptedProperties:
        trackingPropertiesV2?.encryptedProperties != null
          ? [trackingPropertiesV2?.encryptedProperties]
          : [],
    };
  }
);

export const getShopTrackingProperties = createSelector(
  (state: HotelsState) => state,
  (state: HotelsState) =>
    state.hotels.hotelsShop.shop?.trackingPropertiesV2 ?? null
);

export const getHotelAvailabilityTrackingProperties = createSelector(
  HotelsAvailabilitySelectors.getSearchFormDateRange,
  HotelsAvailabilitySelectors.getSort,
  HotelsAvailabilitySelectors.getStarRatingFilter,
  HotelsAvailabilitySelectors.getPriceFilter,
  HotelsAvailabilitySelectors.getCancellationFilter,
  HotelsAvailabilitySelectors.getAmenitiesFilter,
  HotelsAvailabilitySelectors.getHotels,
  HotelsAvailabilitySelectors.getSearchFormGuests,
  HotelsAvailabilitySelectors.amountOfSelectedFilters,
  HotelsAvailabilitySelectors.getRequestedSearch,
  HotelsAvailabilitySelectors.getStayTypeFilter,
  HotelsAvailabilitySelectors.getTotalPropertyCount,
  HotelsAvailabilitySelectors.getAvailabilityTrackingProperties,
  HotelsAvailabilitySelectors.getAvailabilityLodgingTypeCount,
  HotelsAvailabilitySelectors.getHomeAmenitiesFilter,
  HotelsAvailabilitySelectors.getHomeRoomsAndBedsFilter,
  getLodgingAvailabilityTrackingProperties
);

export const getHotelShopAvailabilityTrackingProperties = createSelector(
  HotelsShopSelectors.getShopSearch,
  HotelsAvailabilitySelectors.getRequestedSearch,
  (
    shopSearch: ShopSearch | undefined,
    requestedSearch: SearchDetails | undefined
  ): HotelShopAvailabilityTrackingProperties => {
    if (shopSearch == null && requestedSearch == null) {
      logNotFoundTrackingProperties(
        "getHotelShopAvailabilityTrackingProperties",
        "hotels"
      );
      return fallbackHotelShopAvailabilityTrackingProperties;
    }
    const dateRange = shopSearch?.dateRange ?? requestedSearch?.dateRange;
    const guests = shopSearch?.guests ?? requestedSearch?.guests;
    const rooms = shopSearch?.rooms ?? requestedSearch?.rooms ?? 1;
    if (dateRange == null || guests == null) {
      // This should not happen. It is for TypeScript to calm down.
      logNotFoundTrackingProperties(
        "getHotelShopAvailabilityTrackingProperties",
        "hotels"
      );
      return fallbackHotelShopAvailabilityTrackingProperties;
    }

    return {
      properties: {
        check_in_date: dateRange.from,
        check_out_date: dateRange.until,
        los: dayjs(dateRange.until).diff(dayjs(dateRange.from), "day"),
        adults_count: guests.adults,
        children_count: guests.children.length,
        children_ages: guests.children,
        // for virgin-au
        rooms: rooms,
        // for power-loader
        room_count: rooms,
        advance: dayjs(dateRange.from).diff(dayjs(), "day"),
      },
    };
  }
);

export type HotelsSelectedTrackingProperties = {
  hotelShop: HotelShopTrackingProperties;
  hotelShopAvailability: HotelShopAvailabilityTrackingProperties;
  selectedRoomEntity: HotelSelectedRoomEntityProperties;
  selectedRoomRateEntity: HotelSelectedRoomRateEntityProperties;
  shopPriceChange: { price_change: number };
  shopResponseEntity: TrackingProperties | null;
  hotelAvailability: HotelAvailabilityTrackingProperties;
};

export const getHotelTrackingProperties = createSelector(
  getHotelShopTrackingProperties,
  getHotelShopAvailabilityTrackingProperties,
  getSelectedRoomTrackingProperties,
  getSelectedRoomRateTrackingProperties,
  getShopPriceChange,
  getShopTrackingProperties,
  getHotelAvailabilityTrackingProperties,
  (
    shop: HotelShopTrackingProperties,
    shopAvailability: HotelShopAvailabilityTrackingProperties,
    selectedRoom: HotelRoomTrackingProperties,
    selectedRoomRate: HotelRoomRateTrackingProperties,
    shopPriceChange: { price_change: number },
    shopResponse: TrackingProperties | null,
    availability: HotelAvailabilityTrackingProperties
  ): HotelsSelectedTrackingProperties => {
    return {
      hotelShop: shop,
      hotelShopAvailability: shopAvailability,
      selectedRoomEntity: selectedRoom,
      selectedRoomRateEntity: selectedRoomRate,
      shopPriceChange,
      shopResponseEntity: shopResponse,
      hotelAvailability: availability,
    };
  }
);

export const getTrackingPropertiesForRoomRate = (
  rooms: RoomInfoProducts[],
  roomRate: RoomProduct
) => {
  const roomInfo = rooms.find((room) =>
    room.products.some((product) => product.rateId === roomRate.rateId)
  );

  const selectedIndex = getSelectedRoomIndexFromRooms(roomRate, rooms);
  return {
    selectedIndex,
    ...getTrackingPropertiesFromRoomInfo(roomInfo),
    ...getRoomRateTrackingProperties(roomRate),
  };
};
